import { basePostHogClientOptions } from '@bazak/analytics/client';

import { IS_LOCAL_ENV, IS_TRACKING_ENABLED, POSTHOG_HOST } from '@/common/constants';
import { isAnalyticsFreePath } from '@/lib/analytics/analytics-free-path';
import { attachPostHog, forgetExposure } from '@/lib/analytics/posthog-stand-in';
import { PARENT_ID_SAVED_PARAM } from '@/lib/constants/widget-events';
import { EARLY_ERROR_AT, type HeldEvent } from '@/lib/early-analytics/types';
import { serverFlagsStand } from '@/lib/flags/types';
import { isInIframe } from '@/lib/utils/dom';
import { clearUrlParam, getUrlParam } from '@/lib/utils/url-params';

import type { CaptureResult, PostHog } from 'posthog-js';

// The chat takes over from the early-input script (`take()`) once it is interactive; a page with
// no chat (an error page, the embedding studio) only ever reaches `load`.
const READY_ATTRIBUTE = 'data-early-input';
const COMPOSER_INPUT = '[data-composer-input]';
// A page whose chat never takes over (its code failed to load) still starts analytics, this long
// after `load`.
const CHAT_WAIT_AFTER_LOAD_MS = 10_000;
// Bounds the wait for an idle moment, so a reply that streams from the first second doesn't starve
// analytics. Safari has no `requestIdleCallback`; every iOS visitor takes the timer.
const IDLE_TIMEOUT_MS = 2_000;

const early = window.__chatEarly;

if (
  IS_TRACKING_ENABLED &&
  process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  !isAnalyticsFreePath(window.location.pathname) &&
  early
) {
  const inIframe = isInIframe();
  // Settled before the URL loses `parentDistinctId`, which it reads.
  early.visitor();
  const isParentDistinctIdSaved = getUrlParam(PARENT_ID_SAVED_PARAM) !== '0';
  if (getUrlParam('parentDistinctId')) {
    // Keep it out of Referer headers, document.referrer snapshots, and error-report URLs.
    clearUrlParam('parentDistinctId');
    clearUrlParam(PARENT_ID_SAVED_PARAM);
  }

  whenChatReady(function loadAnalytics() {
    void import('posthog-js').then(({ default: posthog }) => {
      startAnalytics({ posthog, early, inIframe, linkParent: isParentDistinctIdSaved });
    });
  });
}

function startAnalytics({
  posthog,
  early,
  inIframe,
  linkParent,
}: {
  posthog: PostHog;
  early: NonNullable<Window['__chatEarly']>;
  inIframe: boolean;
  linkParent: boolean;
}) {
  const visitor = early.visitor();
  const session = early.session();
  const serverFlags = early.flags;
  const flagsStand = serverFlagsStand({ flags: serverFlags, distinctId: visitor.distinctId });
  // Where the page has visitor flags but they don't stand, the browser asks PostHog after load,
  // for our flags only, so variants assigned in code are never stamped over.
  const askForFlags = Boolean(serverFlags) && !flagsStand;

  function replay(held: HeldEvent) {
    posthog.capture(
      held.event,
      { ...drained.arrival, ...held.properties },
      { timestamp: new Date(held.at), uuid: held.uuid },
    );
  }
  // Drained before init, so a page view the beacon already sent gets its page leave from the
  // start-up script and posthog doesn't send a second. Its registered properties carry `environment`
  // and the tenant.
  const drained = early.drain(replay);

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    ...basePostHogClientOptions({ host: POSTHOG_HOST }),
    advanced_disable_feature_flags: !askForFlags,
    advanced_disable_feature_flags_on_first_load: !askForFlags,
    ...(askForFlags && { flag_keys: Object.keys(serverFlags!.values) }),
    // In-iframe (widget): the host page owns recording/pageviews/autocapture; the widget starts its
    // own recording when opened. A site with recording off says so on <html>.
    disable_session_recording:
      inIframe || document.documentElement.dataset.sessionRecording === 'off',
    // The page view is the one held from the visitor's arrival, with the URL they arrived on.
    capture_pageview: false,
    capture_pageleave: !inIframe && !drained.pageviewBeaconed,
    autocapture: !inIframe,
    before_send: dropOrRetimeException,
    bootstrap: {
      ...visitor.bootstrap,
      ...(session.minted && { sessionID: session.id }),
      // posthog puts flags stored on an earlier visit on every event and, with flags off, never
      // refreshes them; the page's answers (or every visitor flag at its default) replace them.
      ...(!askForFlags && {
        featureFlags: flagsStand ? serverFlags!.values : early.flagDefaults,
      }),
    },
  });

  const sameVisitor = posthog.get_distinct_id() === visitor.distinctId;

  posthog.register(drained.registered);
  if (drained.pageview) replay(drained.pageview);
  for (const held of drained.events) {
    // An exposure counts for the visitor the flags were worked out for, and only for them.
    if (held.event === '$feature_flag_called' && (!flagsStand || !sameVisitor)) {
      forgetExposure({
        key: String(held.properties.$feature_flag),
        distinctId: visitor.distinctId,
        value: String(held.properties.$feature_flag_response),
      });
      continue;
    }
    replay(held);
  }

  const { exceptions } = posthog;
  early.handOver(function reportEarlyError({ error, at, handled, properties }) {
    exceptions?.sendExceptionEvent({
      ...drained.arrival,
      ...properties,
      ...exceptions.buildProperties(error, { handled }),
      [EARLY_ERROR_AT]: at,
    });
  });

  attachPostHog(posthog);

  if (visitor.linkParentDistinctId && linkParent) {
    linkParentDistinctId({
      posthog,
      parentDistinctId: visitor.linkParentDistinctId,
      tenantKey: document.documentElement.dataset.sameSiteEmbed ?? early.tenantKey,
    });
  }

  // The chat already owns its threads by the settled id; a different one here means posthog
  // records this visitor under another person, and their next visit may not find this chat.
  // An empty server id means the server made no prediction (a frame with no id to go on).
  const serverPredictionDiffers = Boolean(
    serverFlags?.distinctId && serverFlags.distinctId !== visitor.distinctId,
  );
  if (!sameVisitor || serverPredictionDiffers) {
    posthog.capture('analytics_visitor_id_mismatch', {
      settledSource: visitor.source,
      posthogDiffers: !sameVisitor,
      serverPredictionDiffers,
    });
  }
}

function dropOrRetimeException(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  // A developer's own machine; production error tracking pages people.
  if (IS_LOCAL_ENV) return null;
  const earlyErrorAt = event.properties?.[EARLY_ERROR_AT];
  // posthog stamps the time it was handed the error; the page threw it earlier.
  if (typeof earlyErrorAt === 'number') event.timestamp = new Date(earlyErrorAt);
  const exceptions = event.properties?.$exception_list;
  const isResizeObserverNoise =
    Array.isArray(exceptions) &&
    exceptions.some((e: { value?: string }) => e.value && /ResizeObserver loop/.test(e.value));
  return isResizeObserverNoise ? null : event;
}

function whenChatReady(start: () => void) {
  let started = false;
  const begin = () => {
    if (started) return;
    started = true;
    observer.disconnect();
    window.removeEventListener('load', onLoad);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
    } else {
      setTimeout(start, 0);
    }
  };
  const root = document.documentElement;
  const chatSettled = () => {
    const phase = root.getAttribute(READY_ATTRIBUTE);
    return phase === 'ready' || phase === 'failed';
  };
  // `load` usually comes before the chat is ready, so it decides only where there is no chat.
  const onLoad = () => {
    if (document.querySelector(COMPOSER_INPUT)) setTimeout(begin, CHAT_WAIT_AFTER_LOAD_MS);
    else begin();
  };
  const observer = new MutationObserver(() => {
    if (chatSettled()) begin();
  });
  observer.observe(root, { attributes: true, attributeFilter: [READY_ATTRIBUTE] });
  window.addEventListener('load', onLoad);
  if (chatSettled()) begin();
  else if (document.readyState === 'complete') onLoad();
}

/** Joins the parent's earlier analytics to the preserved id without making the parent id live. */
function linkParentDistinctId({
  posthog,
  parentDistinctId,
  tenantKey,
}: {
  posthog: PostHog;
  parentDistinctId: string;
  tenantKey: string;
}) {
  const currentDistinctId = posthog.get_distinct_id();
  // posthog turns alias(x, x) into identify(x).
  if (parentDistinctId === currentDistinctId) return;
  // `$create_alias` carries only the super properties registered so far, which by now include
  // the chat's.
  posthog.register({ isWidget: true, tenantKey, tenant: tenantKey });
  posthog.alias(parentDistinctId, currentDistinctId);
}
